import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCelebrateStafferPetAward } from "@/hooks/useStafferPets";
import { guacHttpClient } from "@/lib/http";
import {
  AgenticScoreStatus,
  DashboardData,
  DeleteSearchInterestRequest,
  HttpResponse,
  SearchInterest,
  UpsertSearchInterestRequest,
} from "@/types";

const SEARCH_INTERESTS_PATH = "search-interests";
// 15s
const AGENTIC_SCORE_POLL_INTERVAL_MS = 15_000;
// Interests also change outside this client: nova's top-search sync seeds
// top candidates on a schedule, and the dashboard writes to the same rows.
// Without a refetch the review count drifts from what the server holds.
const SEARCH_INTERESTS_REFRESH_MS = 60_000;

export const searchInterestsQueryKey = (jobId: string) =>
  ["search-interests", jobId] as const;

// Single hook owning all search-interest CRUD; mutations keep the job-scoped
// list cache in sync so callers never wire cache logic themselves.
export const useSearchInterests = (jobId: string) => {
  const queryClient = useQueryClient();
  const celebrateStafferPetAward = useCelebrateStafferPetAward();
  const queryKey = searchInterestsQueryKey(jobId);

  /* Database CRUD Operations */
  // Read
  const {
    data: searchInterests,
    isPending,
    isError,
  } = useQuery({
    // Hosts that derive the job id from another query pass "" while it loads.
    enabled: !!jobId,
    queryKey: searchInterestsQueryKey(jobId),
    queryFn: async () => {
      const res = await guacHttpClient.get<
        SearchInterest[],
        HttpResponse<SearchInterest[]>
      >(SEARCH_INTERESTS_PATH, { params: { jobId } });
      return res.data;
    },
    // Fast poll while any agentic score or AI summary is still being written;
    // slow poll otherwise so server-side seeding shows up without a remount.
    refetchInterval: (query) =>
      query.state.data?.some(
        (interest) =>
          interest.agenticScoreStatus === AgenticScoreStatus.PENDING ||
          interest.aiSummaryStatus === AgenticScoreStatus.PENDING,
      )
        ? AGENTIC_SCORE_POLL_INTERVAL_MS
        : SEARCH_INTERESTS_REFRESH_MS,
  });

  const setInterests = (
    updater: (interests: SearchInterest[]) => SearchInterest[],
  ) => {
    queryClient.setQueryData<SearchInterest[]>(queryKey, (cached) =>
      updater(cached ?? []),
    );
  };

  // Keep every cached dashboard's per-job searchInterestTalentIds in step so
  // the search column count updates without refetching the dashboard.
  const syncDashboardInterest = (talentId: number, isInterested: boolean) => {
    queryClient.setQueriesData<DashboardData>(
      { queryKey: ["dashboard"] },
      (data) => {
        if (!data) return data;
        return {
          ...data,
          jobs: data.jobs.map((job) => {
            if (job.id !== jobId) return job;
            const withoutTalent = job.searchInterestTalentIds.filter(
              (id) => id !== talentId,
            );
            return {
              ...job,
              searchInterestTalentIds: isInterested
                ? [...withoutTalent, talentId]
                : withoutTalent,
            };
          }),
        };
      },
    );
  };

  const getOptimisticInterest = (
    request: UpsertSearchInterestRequest,
  ): SearchInterest => {
    const timestamp = new Date().toISOString();
    return {
      ...request,
      agenticScore: null,
      agenticScoreJustification: null,
      agenticScoreStatus: null,
      aiSummaryShort: null,
      aiSummaryStatus: null,
      commuteDurationSeconds: null,
      sessionId: request.sessionId ?? null,
      deletedAt: null,
      createdAt: timestamp,
      isTopCandidate: false,
      updatedAt: timestamp,
      userId: 0,
    };
  };

  // Upsert (guac POST creates or revives/updates in one call)
  const { mutate: upsertSearchInterest } = useMutation({
    mutationFn: async (request: UpsertSearchInterestRequest) => {
      const res = await guacHttpClient.post<
        SearchInterest,
        HttpResponse<SearchInterest>,
        UpsertSearchInterestRequest
      >(SEARCH_INTERESTS_PATH, request);
      return res.data;
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey });
      const previousInterests =
        queryClient.getQueryData<SearchInterest[]>(queryKey) ?? [];
      const optimisticInterest = getOptimisticInterest(request);
      setInterests((interests) => [
        ...interests.filter(
          (interest) => interest.talentId !== request.talentId,
        ),
        optimisticInterest,
      ]);
      return { previousInterests };
    },
    onError: (_error, _request, context) => {
      queryClient.setQueryData(queryKey, context?.previousInterests ?? []);
    },
    onSuccess: ({ stafferPetAward, stafferPetGoalAward, ...upserted }) => {
      setInterests((interests) => [
        ...interests.filter(
          (interest) =>
            interest.jobId !== upserted.jobId ||
            interest.talentId !== upserted.talentId,
        ),
        upserted,
      ]);
      syncDashboardInterest(upserted.talentId, upserted.isInterested);
      // Per-10 milestone coin and weekly goal bonus, both paid server-side on
      // the interest that earned them.
      celebrateStafferPetAward(stafferPetAward);
      celebrateStafferPetAward(stafferPetGoalAward);
    },
  });

  // Delete
  const { mutate: deleteSearchInterest } = useMutation({
    mutationFn: async (request: DeleteSearchInterestRequest) => {
      const res = await guacHttpClient.delete<
        SearchInterest,
        HttpResponse<SearchInterest>
      >(SEARCH_INTERESTS_PATH, { data: request });
      return res.data;
    },
    onMutate: async (request) => {
      await queryClient.cancelQueries({ queryKey });
      const previousInterests =
        queryClient.getQueryData<SearchInterest[]>(queryKey) ?? [];
      setInterests((interests) =>
        interests.filter((interest) => interest.talentId !== request.talentId),
      );
      return { previousInterests };
    },
    onError: (_error, _request, context) => {
      queryClient.setQueryData(queryKey, context?.previousInterests ?? []);
    },
    onSuccess: (deleted) => {
      setInterests((interests) =>
        interests.filter(
          (interest) =>
            interest.jobId !== deleted.jobId ||
            interest.talentId !== deleted.talentId,
        ),
      );
      syncDashboardInterest(deleted.talentId, false);
    },
  });

  return {
    searchInterests,
    isPending,
    isError,
    upsertSearchInterest,
    deleteSearchInterest,
  };
};
